import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import PrivateRoute from 'utils/privateRoute';

import Icon from 'components/base/Icon';
import { H1 } from 'components/base/Text';

import Account from 'layouts/shared/Account';

import ClientState, { OPEN } from 'ClientState';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.modal === OPEN && css`
    min-height: 100vh;
    max-height: 100vh;
    max-width: 100vw;
    overflow: hidden;
  `}

  @media (min-width: 60em) {
    display: grid;
    grid-template-columns: 15.5rem 1fr;
  }
  @media (min-width: 88em) {
    grid-template-columns: 18rem 1fr;
  }

  @supports not (display: grid) {
    display: flex;
    flex-direction: column;
    @media (min-width: 60em) {
      flex-direction: row;
    }
  }
`;

const enterSidebarLeft = keyframes`
  0% {
    transform: translateX(-1.5rem);
  }
`;
const enterSidebarDown = keyframes`
  0% {
    transform: translateY(-1.5rem);
  }
`;

const Sidebar = styled.div`
  animation: ${enterSidebarDown} 600ms var(--quintOut);
  background-color: var(--blue-gray);
  display: flex;
  flex-direction: column;
  height: auto;
  overflow: hidden;
  position: relative;
  min-height: auto;
  transition: transform 400ms ease;
  width: 100%;
  ${props => props.modal === OPEN && css`
    transform: translateY(-100%);
  `}

  @media (min-width: 60em) {
    animation: ${enterSidebarLeft} 1000ms var(--quintOut);
    display: block;
    height: initial;
    min-height: 100vh;
    width: 15.5rem;
    ${props => props.modal === OPEN && css`
      transform: translateX(-100%);
    `}
  }
  @media (min-width: 88em) {
    width: 18rem;
  }
`;

const Center = styled.div`
  align-items: center;
  color: var(--white);
  display: flex;
  flex-direction: row;
  margin: auto;
  position: relative;
  z-index: 2;

  h1 {
    text-align: center;
    margin-bottom: 0.4em;
  }

  @media (min-width: 60em) {
    display: block;
    position: fixed;
    h1 {
      margin-bottom: initial;
    }
  }
`;

const Flex = styled.div`
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  position: relative;
  margin-bottom: -0.5em;
  width: inherit;
  z-index: 1;
  @media (min-width: 38em) {
    margin-bottom: 0;
  }
  @media (min-width: 60em) {
    height: calc(100vh - 3.25rem);
    margin-bottom: -3.9em;
    position: fixed;
  }
`;

const Envy = styled.div`
  margin: auto 1em auto auto;

  svg {
    color: var(--green);
    display: block;
    height: 1.375rem;
    margin: 0 auto;
    width: 1.375rem;
  }

  @media (min-width: 60em) {
    margin: initial;
    
    svg {
      height: 2.25rem;
      width: 2.25rem;
    }
  }
`;

const Background = styled.div`
  height: 100%;
  overflow: initial;
  position: absolute;
  width: inherit;
  @media (min-width: 60em) {
    overflow: hidden;
    position: relative;
  }
`;

const enterBackgroundSquare1 = keyframes`
  from {
    transform: translateX(12rem) rotate(0deg);
  }
`;
const enterBackgroundSquare2 = keyframes`
  from {
    transform: translateX(-12rem) rotate(0deg);
  }
`;

const BackgroundSquare1 = styled.div`
  animation: ${enterBackgroundSquare1} 600ms linear;
  background: rgba(255, 255, 255, 0.06);
  height: 18.5rem;
  left: inherit;
  position: absolute;
  right: -18rem;
  top: -4rem;
  transform: translateX(0) rotate(-35deg);
  width: 18.5rem;
  z-index: 1;
  outline: 1px solid transparent;
  @media (min-width: 60em) {
    left: 12.5rem;
    right: initial;
    top: 0;
  }
`;

const BackgroundSquare2 = styled.div`
  animation: ${enterBackgroundSquare2} 600ms linear;
  background: rgba(255, 255, 255, 0.06);
  bottom: 1rem;
  height: 18.5rem;
  left: -10rem;
  position: absolute;
  transform: translateX(0) rotate(52deg);
  width: 18.5rem;
  z-index: 1;
  outline: 1px solid transparent;
  @media (min-width: 60em) {
    bottom: 6.25rem;
    left: -15.5rem;
    transform: translateX(0) rotate(20deg);
  }
`;

const HomeLayout = ({ component: Component, data, ...rest }) => (
  <PrivateRoute
    {...rest}
    component={matchProps => (
      <ClientState>
        {(context) => (
          <Wrapper modal={context.modal}>
            <Sidebar modal={context.modal}>
              <Flex>
                <Center>
                  <Envy>
                    <Icon id="envy-logo" title="Envy Labs" />
                  </Envy>
                  <H1>Client Dashboard</H1>
                </Center>
                <Background>
                  <BackgroundSquare1 />
                  <BackgroundSquare2 />
                </Background>
              </Flex>
              <Account envySidebar />
            </Sidebar>
            <Component {...matchProps} context={context} />
          </Wrapper>
        )}
      </ClientState>
    )}
  />
);

export default HomeLayout;



// WEBPACK FOOTER //
// ./src/layouts/HomeLayout.js